import {
    Box,
    Container,
    Typography,
    Divider,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Avatar,
    Tooltip,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState, useRef } from "react";
import BusinessIcon from "@mui/icons-material/Business";

import { PARTICIPANT_LIST, SEARCH_PARTICIPANTS } from "../../config/settings";
import { useTranslator } from "../../context/TranslatorContext";
import validatedImg from "../../assets/icons/EU_controlled_icon.svg";
import registeredImg from "../../assets/icons/EU-emblem_RGB.svg";

export default function ParticipantList({
    search,
    euRegistered,
    euControlled,
    country,
    sortField,
    sortOrder,
    page,
    selectedCountry,
    onCountryHover,
    onDataLoad
}) {
    const { language, messages } = useTranslator();
    const [participantList, setParticipantList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const countryRefs = useRef({});
    const PAGE_SIZE = 1000 ;
    useEffect(() => {
        const params = new URLSearchParams();
        if (language && language !== "en") {
            params.append("lang", language);
        }
        if (search) {
            params.append("search", search);
        }
        if (euRegistered) {
            params.append("terms__eu_registered", "true");
        }
        if (euControlled) {
            params.append( "terms__eu_controlled", "true");
        }
        if (country) {
            params.append("terms__country_of_registration",country);
        }
        if (sortField) {
            params.append("sortField", sortField);
        }
        if (sortOrder) {
            params.append("sortOrder", sortOrder );
        }

        if (page) {
            params.append("page", page);
        }
        const url = `${SEARCH_PARTICIPANTS}?${params.toString()}`;
        setLoading(true);
        axios
            .get(url)
            .then((response)=>{
                const participants =
                    response.data.hits.hits.map(
                        hit => ({
                            id: hit._id,
                            ...hit._source
                        })
                    );
                const totalHits = response.data.hits.total.value || response.data.hits.total || 0;
                // console.log(totalHits);
                const calculatedTotalPages = Math.ceil(totalHits / PAGE_SIZE);
                setParticipantList(participants);
                // console.log("ES RESPONSE", response.data);
                // console.log("AGGS", response.data.aggregations);
                if(onDataLoad){
                    onDataLoad({
                        participants,
                        facets: response.data.aggregations,
                        totalPages: calculatedTotalPages,
                    });
                }
                setLoading(false);
            })
            .catch((err)=>{
                console.error(err);
                setError(err);
                setLoading(false);
            });

    },[
        language,
        search,
        euRegistered,
        euControlled,
        country,
        sortField,
        sortOrder,
        page,
        onDataLoad
    ]);

    // go to country when selected from map
    useEffect(() => {
        if (selectedCountry && countryRefs.current[selectedCountry]) {
            countryRefs.current[selectedCountry].scrollIntoView({
                behavior: "smooth",
                block: "nearest",
            });
        }
    }, [selectedCountry]);

    // Group participants by country
    const groupedByCountry = participantList.reduce((acc,item)=>{
        const key = item.country_label;
        if(!acc[key]){
            acc[key]={
                label:item.country_label,
                participants:[]
            };
        }
        acc[key].participants.push(item);
        return acc;
    },{});

    // Sort countries alphabetically
    const sortedCountries = Object.keys(groupedByCountry).sort((a, b) =>
        groupedByCountry[a].label.localeCompare(groupedByCountry[b].label)
    );
    return (
        <Container maxWidth="lg" sx={{ mt: 2, mb: 2, px: 0 }}>
            <Box
                component="section"
                sx={{
                    maxHeight: "60vh",
                    overflowY: "auto",
                    pr: { md: 2 },
                }}
            >
                {sortedCountries.map((country) => {
                    const countryGroup = groupedByCountry[country];
                    const isHighlighted = selectedCountry === country;

                    return (
                        <Box key={country} sx={{ mb: 4 }} ref={(el) => (countryRefs.current[country] = el)}>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: isHighlighted ? "primary.600" : "primary.main",
                                    fontWeight: "bold",
                                    mb: 1,
                                    transition: "all 0.3s ease",
                                    backgroundColor: isHighlighted ? "secondary.light" : "transparent",
                                    padding: "4px 8px",
                                    borderRadius: "4px",
                                }}
                                onMouseEnter={() => onCountryHover && onCountryHover(country)}
                                onMouseLeave={() => onCountryHover && onCountryHover(null)}
                            >
                                 {countryGroup.label} ({countryGroup.participants.length})
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            {/* Participant List */}
                            <List sx={{ pt: 0 }}>
                                {countryGroup.participants.map((item, index) => (
                                    <ListItem
                                        key={index}
                                        sx={{
                                            px: 0,
                                            py: 1,
                                            backgroundColor: isHighlighted ? "secondary.100" : "transparent",
                                            transition: "all 0.3s ease",
                                            borderRadius: "4px",
                                            "&:hover": {
                                                backgroundColor: "secondary.light",
                                                cursor: "pointer",
                                            },
                                        }}
                                        onMouseEnter={() => onCountryHover && onCountryHover(country)}
                                        onMouseLeave={() => onCountryHover && onCountryHover(null)}
                                        onClick={() =>
                                            onCountryHover &&
                                            onCountryHover(
                                                selectedCountry === country ? null : country
                                            )
                                        }
                                    >
                                        <ListItemAvatar>
                                            <Avatar
                                                sx={{
                                                    bgcolor: isHighlighted ? "primary.600" : "primary.main",
                                                    width: 36,
                                                    height: 36,
                                                    transition: "all 0.3s ease",
                                                }}
                                            >
                                                <BusinessIcon fontSize="small" />
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Typography variant="body1" fontWeight="medium">
                                                        {item.participant}
                                                    </Typography>

                                                    {(item.eu_controlled === true || item.eu_controlled === "true") && (
                                                        <Tooltip title={messages.eu_controlled} arrow placement="top">
                                                            <Box
                                                                component="img"
                                                                src={validatedImg}
                                                                alt={messages?.eu_controlled_label || "EU Controlled"}
                                                                sx={{ width: 22, height: 22, opacity: 0.82 }}
                                                            /></Tooltip>
                                                    )}
                                                     {(item.eu_registered === true || item.eu_registered === "true") && (
                                                        <Tooltip title={messages.eu_registered} arrow placement="top">
                                                            <Box
                                                                component="img"
                                                                src={registeredImg}
                                                                alt={messages?.eu_registered_label || "EU Registered"}
                                                                sx={{ width: 22, height: 22, opacity: 0.82 }}
                                                            /></Tooltip>
                                                    )}
                                                </Box>
                                            }


                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    );
                })}
            </Box>
        </Container>
    );
}
